import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Grid, Typography, Box, SxProps, Theme } from '@mui/material';
import { textFieldStyles, selectFieldStyles } from 'quoting/new-quote/helpers';
import { SecondaryActionButton } from 'shared/components';
import { FormDateField, FormTextField, SelectField } from 'shared/controls';
import { useService } from 'shared/providers';
import { useQueryRepAgencies, useQueryDirectors } from 'shared/queries';
import { QuoteDocument } from 'shared/types';

const actionButtonStyles: SxProps<Theme> = {
  width: {
    xs: '100%',
    sm: '30%',
    md: '60%',
  },
};

interface Props {
  quote?: QuoteDocument;
}

export const Notes = ({ quote }: Props) => {
  const { appConfig } = useService();
  const dev = appConfig.environment === 'dev';
  const { repAgencies, isLoading, isFetching } = useQueryRepAgencies();
  const { directors, isLoading: isLoadingDirectors, isFetching: isFetchingDirectors } = useQueryDirectors();
  const repAgenciesOptions = dev ? [...repAgencies, 'DEV AGENCY'] : repAgencies;

  const {
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext();

  const saveAsDraft = useCallback(() => {
    setValue('status', 'DRAFT');
  }, [setValue]);

  const saveAsQuote = useCallback(() => {
    setValue('status', 'SUBMITTED & RECEIVED');
  }, [setValue]);

  return (
    <>
      <Grid container justifyContent='space-between'>
        <Typography variant='h6' mb={1}>
          Notes
        </Typography>
      </Grid>
      <Grid container justifyContent='space-between' spacing={{ xs: 2, md: 3 }} mb={2}>
        <Grid item xs={12} sm={12} md={6}>
          <FormTextField sx={textFieldStyles} label='General Notes' name='notes' fullWidth multiline rows={3} />
        </Grid>
        <Grid item xs={12} sm={12} md={6}>
          <FormTextField sx={textFieldStyles} label='Options / ETOS' name='optionsETOS' fullWidth multiline rows={3} />
        </Grid>
      </Grid>
      <Grid container justifyContent='space-between'>
        <Grid container item xs={12} sm={12} md={9} spacing={3}>
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ marginTop: 1 }}>
              <FormTextField
                sx={textFieldStyles}
                name='requestedBy'
                label='Requested By *'
                rules={{ required: { message: 'Requested By is required', value: true } }}
                error={Boolean(errors?.requestedBy)}
                helperText={errors?.requestedBy?.message}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='repAgency'
                label='Rep Agency *'
                options={repAgenciesOptions || []}
                getOptionLabel={opt => opt}
                loading={isLoading || isFetching}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                rules={{ required: { message: 'Rep Agency is required', value: true } }}
                error={Boolean(errors?.repAgency)}
                helperText={errors?.repAgency?.message}
                sx={selectFieldStyles}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='director'
                label='Director *'
                options={directors || []}
                getOptionLabel={opt => opt}
                loading={isLoadingDirectors || isFetchingDirectors}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                rules={{
                  validate: (value: string) => getValues('status') === 'DRAFT' || !!value || 'Director is required',
                }}
                error={Boolean(errors?.director)}
                helperText={errors?.director?.message}
                sx={selectFieldStyles}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ marginTop: 1 }}>
              <FormTextField sx={textFieldStyles} name='createdByEmail' label='Request Created By' disabled={true} />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <FormTextField
                sx={textFieldStyles}
                name='lastModifiedByEmail'
                label='Revision Requested By'
                disabled={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ marginTop: 1 }}>
              <FormDateField sx={textFieldStyles} name='createdOn' label='Request Created On' disabled />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <FormDateField sx={textFieldStyles} name='lastModifiedOn' label='Revision Requested On' disabled />
            </Box>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={3}
          justify-content='flex-end'
          sx={{ display: 'flex', flexFlow: 'column', justifyContent: 'flex-end' }}
        >
          <Grid item sx={{ textAlign: 'right' }} mb={1}>
            <SecondaryActionButton
              actionText='Submit Quote Request'
              type='submit'
              onClick={saveAsQuote}
              sx={actionButtonStyles}
            />
          </Grid>
          <Grid item sx={{ textAlign: 'right' }}>
            <SecondaryActionButton
              actionText='Save as Draft'
              type='submit'
              onClick={saveAsDraft}
              sx={actionButtonStyles}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
